/* eslint-disable react-hooks/exhaustive-deps */
'use client';

import { BackArrow } from '@ahamove/icons';
import { DeliveryType } from '@ahm/api-wrappers-core';
import { useLocationStore } from '@ahm/react-map/store';
import {
  ActionIcon,
  Button,
  Card,
  Group,
  Modal,
  rem,
  ScrollArea,
  Space,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { usePathname } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import TruckPackageDetailsForm from '@/components/modals/package-details-modal/truck-package-details-form';
import { useGetPackageSizeDefinitions } from '@/features/booking/api/master-data/use-get-package-size-definitions';
import { useBillableWeight } from '@/features/booking/api/service/use-billable-weight';
import ItemDescriptionsSection from '@/features/booking/components/bike-package-detail/components/item-descriptions-section';
import TruckBulkyPackageHeader from '@/features/booking/components/truck/truck-package-detail/components/truck-bulky-package-header';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { getServiceType } from '@/lib/utils';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

interface GroupedPackage {
  size: string;
  description: string;
  image_url: string;
  package_in_centimeters?: {
    length: number;
    width: number;
    height: number;
  };
  weights: [number, number][];
}

const DEFAULT_TRUCK_PACKAGE_DETAIL_ITEM = {
  weight: null,
  length: null,
  width: null,
  height: null,
  quantity: 1,
  image_url: [],
  count: 1,
};

interface TruckPackageForm {
  weight?: number | null;
  length?: number | null;
  width?: number | null;
  height?: number | null;
  quantity: number;
  image_url: string[] | string;
}

const sizeLabelMap = {
  XL: 'Nhỏ',
  '2XL': 'Vừa',
  '3XL': 'Lớn',
};

function groupPackages(sizeDefinitions: any[]): GroupedPackage[] {
  if (!sizeDefinitions?.length) {
    return [];
  }

  const grouped = sizeDefinitions.reduce<Record<string, GroupedPackage>>(
    (
      acc,
      {
        size,
        description,
        image_url,
        package_in_centimeters,
        min_weight,
        max_weight,
      }
    ) => {
      if (!acc[size]) {
        acc[size] = {
          size,
          description,
          image_url,
          package_in_centimeters,
          weights: [],
        };
      }
      acc[size].weights.push([min_weight ?? 0, max_weight]);
      return acc;
    },
    {}
  );

  return Object.values(grouped).map((item) => ({
    ...item,
    weights: item.weights.sort((a, b) => a[0] - b[0]),
  }));
}

export default function TruckBulkyPackageDetail() {
  const [isOpenCollapse, setIsOpenCollapse] = useState(true);
  const [sizeState, setSizeState] = useState<string | null>(null);
  const [weightState, setWeightState] = useState<[number, number] | null>(null);

  const {
    packageDetail,
    createOrderError,
    setItemDescriptions,
    // billableWeightData,
    selectedService,
    setPackageDetail,
    setBillableWeightData,
  } = useBookingStore((state) => state);

  const isMobile = useIsMobile();
  const pathname = usePathname();
  const { isTruckService } = useMemo(
    () => getServiceType(pathname),
    [pathname]
  );

  const initialPackageForm = useMemo(() => {
    return {
      package_detail: packageDetail ?? [DEFAULT_TRUCK_PACKAGE_DETAIL_ITEM],
    };
  }, [packageDetail]);

  const [openedModal, { open: openModal, close: closeModal }] =
    useDisclosure(false);

  const { paths } = useLocationStore((state) => state);

  const { data: sizeDefinitionTruckList = [] } = useGetPackageSizeDefinitions(
    DeliveryType.Truck,
    { enabled: isTruckService }
  );

  const groupedPackages = useMemo(
    () => groupPackages(sizeDefinitionTruckList),
    [sizeDefinitionTruckList]
  );

  // ✅ Auto select default size + weight
  useEffect(() => {
    if (groupedPackages && groupedPackages.length > 0) {
      const lastSize = groupedPackages[groupedPackages.length - 1];
      setSizeState(lastSize.size);
      if (lastSize.weights.length) {
        setWeightState(lastSize.weights[lastSize.weights.length - 1]);
      }
    }
  }, [groupedPackages]);

  // ✅ Mapping packageDetail → UI selection
  useEffect(() => {
    if (!packageDetail?.[0] || groupedPackages.length === 0) return;

    const { weight, length, width, height } = packageDetail[0];

    // Map weight → range
    if (weight) {
      const selectedWeights = groupedPackages.flatMap((pkg) =>
        pkg.weights.map((w) => ({ size: pkg.size, range: w }))
      );

      const found = selectedWeights.find(
        ({ range }) => weight >= range[0] && weight <= range[1]
      );

      if (found) {
        setSizeState(found.size);
        setWeightState(found.range);
      }
    }

    // Map dimensions → size
    if (length && width && height) {
      const maxDim = Math.max(length, width, height);

      const foundSize = groupedPackages.find((pkg) => {
        const { length, width, height } = pkg.package_in_centimeters ?? {};
        if (!length || !width || !height) return false;
        const pkgMax = Math.max(length, width, height);
        return maxDim <= pkgMax;
      });

      if (foundSize) {
        setSizeState(foundSize.size);
      }
    }
  }, [packageDetail, groupedPackages]);

  // const [estimateError, setEstimateError] = useState(false);
  // const [isEstimating, setIsEstimating] = useState(false);

  const { mutate: onBillableWeight } = useBillableWeight({
    onSuccess: (res) => {
      if (!res) return;
      const dimensions = [res.width, res.length, res.height].sort(
        (a, b) => a - b
      );
      const packageDetailItem = [
        {
          weight: res.weight,
          height: dimensions[0],
          width: dimensions[1],
          length: dimensions[2],
        },
      ];
      setPackageDetail(packageDetailItem as TruckPackageForm[]);
      setBillableWeightData(res);
    },
    onError: () => {
      // setEstimateError(true);
      // setIsEstimating(false);
      setBillableWeightData(null);
    },
  });

  const packageEstimateBody = useMemo(() => {
    if (!sizeState || !weightState || !selectedService?._id || paths.length < 2)
      return null;

    const selected = groupedPackages.find((item) => item.size === sizeState);
    if (!selected) return null;

    const { length, width, height } = selected.package_in_centimeters ?? {};
    const weight = weightState[1]; // max weight

    return {
      package_dimension_unit: 'cm',
      service_id: selectedService._id,
      package_detail: [
        {
          weight,
          length,
          width,
          height,
          quantity: 1,
        },
      ],
    };
  }, [sizeState, weightState, selectedService?._id, paths.length]);

  // ✅ Debounce call API khi state thay đổi
  useEffect(() => {
    if (!packageEstimateBody) return;
    const timeout = setTimeout(() => {
      // setIsEstimating(true);
      // setEstimateError(false);
      onBillableWeight(packageEstimateBody);
    }, 500);
    return () => clearTimeout(timeout);
  }, [packageEstimateBody, onBillableWeight]);

  const handleSelect = (key: string, value: any) => {
    setItemDescriptions(value);
  };

  const descriptionSize = groupedPackages?.find(
    (item) => item.size === sizeState
  )?.description;

  const weightsOfCurrentSize =
    groupedPackages?.find((item) => item.size === sizeState)?.weights ?? [];

  if (!paths || paths.length < 2) return null;

  const getButtonStyle = (active: boolean) => ({
    width: rem(96),
    height: rem(40),
    borderRadius: '40px',
    borderColor: active
      ? 'var(--mantine-color-primary-6)'
      : 'var(--mantine-color-neutral-4)',
    backgroundColor: active ? 'var(--mantine-color-primary-2)' : 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  });

  return (
    <Card
      withBorder
      radius={12}
      p={0}
      mt={0}
      style={{
        borderColor:
          createOrderError === 'package-detail'
            ? 'var(--mantine-color-primary-6)'
            : 'white',
        background:
          !isOpenCollapse && createOrderError === 'package-detail'
            ? 'var(--mantine-color-red-0)'
            : 'white',
      }}
    >
      <TruckBulkyPackageHeader
        isOpen={isOpenCollapse}
        onToggle={() => setIsOpenCollapse((prev) => !prev)}
      />

      <Stack>
        {/* Kích cỡ */}
        <Stack gap={8} px="md" py={rem(6)}>
          <Group justify="space-between">
            <Group>
              <Title order={isMobile ? 6 : 4} size={isMobile ? 'sm' : 'md'}>
                Kích cỡ{' '}
                <Text component="span" c="red">
                  *
                </Text>
              </Title>
            </Group>
            <Button
              onClick={openModal}
              variant="subtle"
              color="primary"
              justify="flex-start"
              radius={rem(4)}
              size="compact-md"
              h="auto"
              lh={1.5}
              mr={-8}
              styles={{
                inner: {
                  fontSize: isMobile ? rem(14) : rem(16),
                  fontWeight: 700,
                },
              }}
            >
              Xem chi tiết
            </Button>
          </Group>
          <Text h={25} size={isMobile ? 'sm' : 'md'}>
            {descriptionSize}
          </Text>
          <Group gap={8}>
            {groupedPackages?.map(({ size }) => (
              <Button
                key={size}
                variant={sizeState === size ? 'filled' : 'outline'}
                c={sizeState === size ? 'primary' : 'neutral.7'}
                onClick={() => {
                  setSizeState(size);
                  const pkg = groupedPackages.find((x) => x.size === size);
                  if (pkg?.weights.length) {
                    setWeightState(pkg.weights[pkg.weights.length - 1]);
                  }
                }}
                p="6px 12px"
                style={getButtonStyle(sizeState === size)}
              >
                <Text fw={sizeState === size ? 600 : 500}>
                  {sizeLabelMap[size] ?? size}
                </Text>
              </Button>
            ))}
          </Group>
        </Stack>

        {/* Khối lượng */}
        <Stack gap={8} px="md" py={rem(6)}>
          <Group>
            <Title order={isMobile ? 6 : 4} size={isMobile ? 'sm' : 'md'}>
              Khối lượng (kg){' '}
              <Text component="span" c="red">
                *
              </Text>
            </Title>
          </Group>

          <Group gap={8}>
            {weightsOfCurrentSize.map((range) => {
              const isActive =
                weightState &&
                weightState[0] === range[0] &&
                weightState[1] === range[1];

              return (
                <Button
                  key={`${range[0]}-${range[1]}`}
                  variant={isActive ? 'filled' : 'outline'}
                  c={isActive ? 'primary' : 'neutral.7'}
                  onClick={() => setWeightState(range)}
                  p="6px 12px"
                  style={getButtonStyle(isActive)}
                >
                  <Text fw={isActive ? 600 : 500}>
                    {`${range[0]} - ${range[1]}`}
                  </Text>
                </Button>
              );
            })}
          </Group>
        </Stack>

        <ItemDescriptionsSection onChange={handleSelect} />
      </Stack>

      <Modal
        opened={openedModal}
        onClose={closeModal}
        title={
          <Group gap="xl" align="center" justify="space-between" w="100%">
            {isMobile ? (
              <ActionIcon
                size={rem(24)}
                onClick={closeModal}
                variant="transparent"
              >
                <BackArrow width={24} height={24} />
              </ActionIcon>
            ) : null}
            <Title
              order={2}
              size="h4"
              flex={1}
              ta={isMobile ? 'center' : 'left'}
            >
              Thông tin hàng hóa
            </Title>
            <Space />
          </Group>
        }
        withCloseButton={!isMobile}
        fullScreen={isMobile}
        closeOnEscape={false}
        scrollAreaComponent={ScrollArea.Autosize}
        styles={{
          header: {
            backgroundColor: 'white',
            minHeight: rem(44),
            padding: isMobile
              ? `${rem(10)} ${rem(16)}`
              : `${rem(16)} ${rem(16)}`,
            border: isMobile
              ? `1px solid var(--mantine-color-neutral-1)`
              : 'none',
          },
          title: { width: '100%' },
          close: {
            '--cb-icon-size': rem(32),
            height: rem(32),
            width: rem(32),
            borderRadius: rem(32),
          },
          content: { backgroundColor: 'white' },
          body: { padding: 0, minHeight: 500 },
        }}
      >
        <TruckPackageDetailsForm
          innerProps={{
            initialData: initialPackageForm as unknown as TruckPackageForm[],
          }}
          onClose={closeModal}
        />
      </Modal>
    </Card>
  );
}
